/**
 * Story Snapshot Utility
 * 
 * Manages versioned persistence of story state to ensure export pages
 * render with correct data and never fall back to defaults silently.
 */

import type { SystemRecInput, SystemRecOutput } from '@core/types';
import type { NormalizedOutput } from '@core/normalizeOutput';
import type { CollectedData } from '../context/RecommendationContext';

const STORAGE_KEY = 'storySnapshot';
// Snapshot schema version - increment when snapshot structure changes
// This is separate from app version to allow independent versioning of data format
const CURRENT_VERSION = '1.0.0';

export interface StorySnapshot {
  /** Schema version for compatibility checks */
  version: string;
  
  /** When this snapshot was created */
  updatedAt: string;

  /** Metadata about the snapshot and environment */
  meta?: {
    createdAt: string;
    route: string;
    appVersion: string;
    env: string;
    timezone: string;
    sessionId: string;
  };

  /** Optional notes stored alongside the snapshot */
  notes?: string;

  /** Data contract version when this snapshot was saved (ATLAS_DATA_CONTRACT_VERSION) */
  contractVersion?: string;

  /**
   * True when this document was loaded without a contractVersion (pre–Data Contract)
   * and was automatically stamped with the current ATLAS_DATA_CONTRACT_VERSION on load.
   */
  migratedFromLegacy?: boolean;
  
  /** Raw collected data from story flow */
  rawInput: CollectedData;
  
  /** Engine computation output */
  engineOutput: {
    input: SystemRecInput;
    output: SystemRecOutput;
    normalizedOutput?: NormalizedOutput;
    inputHash?: string;
    generatedAt?: string;
  } | null;
  
  /** Currently selected option ID (if any) */
  selectedOptionId?: string | null;

  /** Premium add-ons selected for the currently selected option only */
  selectedOptionAddOns?: string[];

  /** Expert-defined option slot overrides for A / A+ / B portal display */
  optionSlots?: {
    /** Option A override — ID of the considered option to use as primary (default: output.primary) */
    optionAId?: string;
    /** Option B override — ID of the considered option to use as alternative (default: output.alternatives[0]) */
    optionBId?: string;
    /** A+ premium scope flags — which premium interventions to include */
    premiumScope?: string[];
  };
}

export function buildStorySnapshot(snapshot: Omit<StorySnapshot, 'version' | 'updatedAt'>): StorySnapshot {
  return {
    ...snapshot,
    version: CURRENT_VERSION,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Save story snapshot to sessionStorage
 */
export function saveStorySnapshot(snapshot: Omit<StorySnapshot, 'version' | 'updatedAt'>): void {
  const fullSnapshot = buildStorySnapshot(snapshot);
  
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fullSnapshot));
  } catch (error) {
    console.error('Failed to save story snapshot:', error);
  }
}

/**
 * Load story snapshot from sessionStorage
 * Returns null if missing or version mismatch
 */
export function loadStorySnapshot(): StorySnapshot | null {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (!saved || saved === 'undefined' || saved === 'null') {
      return null;
    }
    
    const snapshot = JSON.parse(saved) as StorySnapshot;
    
    // Version check - for now, just log mismatches but don't fail
    // In production, you might want stricter versioning
    if (snapshot.version !== CURRENT_VERSION) {
      console.warn(`Story snapshot version mismatch: ${snapshot.version} !== ${CURRENT_VERSION}`);
      // For now, still return it - can be made stricter later
    }
    
    return snapshot;
  } catch (error) {
    console.error('Failed to load story snapshot:', error);
    return null;
  }
}

/**
 * Clear story snapshot from sessionStorage
 */
export function clearStorySnapshot(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Check if a valid snapshot exists
 */
export function hasStorySnapshot(): boolean {
  return loadStorySnapshot() !== null;
}

/**
 * Get snapshot age in milliseconds
 */
export function getSnapshotAge(): number | null {
  const snapshot = loadStorySnapshot();
  if (!snapshot) return null;
  
  const now = new Date().getTime();
  const updated = new Date(snapshot.updatedAt).getTime();
  return now - updated;
}

/**
 * Check if snapshot is stale (older than threshold)
 * Default threshold: 24 hours
 */
export function isSnapshotStale(thresholdMs: number = 24 * 60 * 60 * 1000): boolean {
  const age = getSnapshotAge();
  if (age === null) return true;
  return age > thresholdMs;
}
